if (!customElements.get('spec-tooltip')) {
  // Space between the label and the tooltip (the arrow sits in it), and the
  // minimum distance from the viewport edge
  const OFFSET = 8;
  const GUTTER = 16;
  // Keeps the arrow off the rounded corners
  const ARROW_INSET = 12;

  let openTooltip = null;
  let tooltipCount = 0;

  // One set of document listeners for every tooltip on the page
  document.addEventListener('pointerdown', (event) => {
    if (openTooltip && !openTooltip.contains(event.target)) openTooltip.hide();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openTooltip) openTooltip.hide();
  });
  // Capture so scrolling the compare cards or accordion closes it too
  window.addEventListener('scroll', () => openTooltip?.hide(), { capture: true, passive: true });
  window.addEventListener('resize', () => openTooltip?.hide());

  class SpecTooltip extends HTMLElement {
    // Init in connectedCallback, not the constructor: the specs render inside the
    // Vue #app and get swapped in via innerHTML on variant change (see CLAUDE.md)
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.trigger = this.querySelector('.spec-tooltip__trigger');
      this.content = this.querySelector('.spec-tooltip__content');
      if (!this.trigger || !this.content) return;

      tooltipCount += 1;
      this.content.id = `SpecTooltip-${tooltipCount}`;
      this.trigger.setAttribute('aria-describedby', this.content.id);
      // Set by a click or tap: stays open until clicked again, a click outside, or Escape
      this.pinned = false;

      // Mouse only: a tap fires pointerenter too, and click handles taps
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.show();
      });
      // Delayed so the pointer can cross the gap onto the tooltip without closing it
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && !this.pinned) this.hideSoon();
      });
      this.trigger.addEventListener('focus', () => {
        if (this.trigger.matches(':focus-visible')) this.show();
      });
      this.trigger.addEventListener('blur', () => {
        if (!this.pinned) this.hide();
      });
      this.trigger.addEventListener('click', () => {
        this.pinned = !this.pinned;
        if (this.pinned) {
          this.show();
        } else {
          this.hide();
        }
      });
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer);
      if (openTooltip === this) openTooltip = null;
    }

    show() {
      clearTimeout(this.hideTimer);
      if (openTooltip && openTooltip !== this) openTooltip.hide();
      openTooltip = this;
      if (!this.content.matches(':popover-open')) this.content.showPopover();
      this.position();
    }

    hide() {
      clearTimeout(this.hideTimer);
      this.pinned = false;
      if (this.content.matches(':popover-open')) this.content.hidePopover();
      if (openTooltip === this) openTooltip = null;
    }

    hideSoon() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), 150);
    }

    // Below the label, left-aligned with it and kept inside the viewport, with the
    // arrow under the label's center. Flips above when there's no room below.
    position() {
      const trigger = this.trigger.getBoundingClientRect();
      const { width, height } = this.content.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;

      const left = Math.max(GUTTER, Math.min(trigger.left, viewportWidth - width - GUTTER));
      let top = trigger.bottom + OFFSET;
      let placement = 'bottom';
      if (top + height > viewportHeight - GUTTER && trigger.top - OFFSET - height > GUTTER) {
        top = trigger.top - OFFSET - height;
        placement = 'top';
      }
      const arrowX = Math.max(ARROW_INSET, Math.min(trigger.left + trigger.width / 2 - left, width - ARROW_INSET));

      this.content.style.left = `${left}px`;
      this.content.style.top = `${top}px`;
      this.content.style.setProperty('--spec-tooltip-arrow-x', `${arrowX}px`);
      this.content.dataset.placement = placement;
    }
  }

  customElements.define('spec-tooltip', SpecTooltip);
}
